import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, api, type QueueReport, type ReportMetrics, type TextMetrics, type TextReport } from '../../api/client';
import { ChartLegend, ColumnChart, LineChart, type ChartSeries } from '../../components/charts';
import { useTenant } from '../../components/Shell';
import { Button, Card, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { clockTime } from '../../lib/format';
import { displayZone, todayIn } from '../../lib/time';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

// --- periods ------------------------------------------------------------------------------------

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'custom', label: 'Custom' }
] as const;
type Preset = (typeof PRESETS)[number]['id'];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dayOf = (s: string) => new Date(`${s}T12:00:00`);

function rangeOf(preset: Preset, from: string | null, to: string | null, today = new Date()): { from: string; to: string } {
  switch (preset) {
    case 'today':
      return { from: iso(today), to: iso(today) };
    case 'yesterday':
      return { from: iso(addDays(today, -1)), to: iso(addDays(today, -1)) };
    case '30d':
      return { from: iso(addDays(today, -29)), to: iso(today) };
    case 'month':
      return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    case 'lastMonth':
      return { from: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: iso(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case 'custom':
      if (from && to && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to)) return { from, to };
      return { from: iso(addDays(today, -6)), to: iso(today) };
    default:
      return { from: iso(addDays(today, -6)), to: iso(today) };
  }
}

// --- formatting ---------------------------------------------------------------------------------

const count = (n: number) => n.toLocaleString();
/**
 * Calls not answered: every call less those answered (3136). Not missed + hung up: the phone
 * system's "missed" counts a call that rang an agent who didn't pick up, even if someone else did,
 * so adding the two could come to more than the calls themselves.
 */
const notAnswered = (m: { offered: number; answered: number }) => Math.max(0, m.offered - m.answered);
const pct = (n: number | null) => (n === null ? '–' : `${Math.round(n * 100)}%`);
/** 0:21, 3:05, 1:02:10. */
export function clock(s: number | null): string {
  if (s === null) return '–';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}
/** 12h 30m, 45m, 30s. */
function span(s: number): string {
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  if (s >= 60) return `${Math.round(s / 60)}m`;
  return `${s}s`;
}
const hourName = (h: number) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
const monthDay = (s: string) => dayOf(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const longDay = (s: string) => dayOf(s).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

export function pointNames(r: Pick<QueueReport, 'by'> & { series: readonly unknown[] }, key: string): { label: string; title: string } {
  if (r.by === 'hour') {
    const h = Number(key);
    return { label: hourName(h).replace(':00', ''), title: `${hourName(h)} to ${hourName((h + 1) % 24)}` };
  }
  const days = r.series.length;
  return { label: days <= 8 ? dayOf(key).toLocaleDateString(undefined, { weekday: 'short' }) : monthDay(key), title: longDay(key) };
}

function rangeTitle(from: string, to: string): string {
  if (from === to) return longDay(from);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${monthDay(from)} – ${dayOf(to).toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })}`;
}

// --- changes against the previous period ---------------------------------------------------------

type Better = 'up' | 'down' | 'none';
/** The change against the previous period: an arrow and the amount, green when better, red when worse. */
function Change({ cur, prev, kind, better, label }: { cur: number | null; prev: number | null; kind: 'count' | 'rate' | 'seconds'; better: Better; label: string }) {
  if (cur === null || prev === null) return <span className="kpi-change">Nothing to compare with</span>;
  const diff = cur - prev;
  const amount =
    kind === 'rate'
      ? Math.abs(Math.round(diff * 100))
      : kind === 'seconds'
        ? Math.abs(Math.round(diff))
        : prev === 0
          ? Math.abs(diff)
          : Math.round(Math.abs(diff / prev) * 100);
  // A change that rounds away is no change.
  if (amount === 0) return <span className="kpi-change">No change</span>;
  const up = diff > 0;
  const tone = better === 'none' ? '' : (up && better === 'up') || (!up && better === 'down') ? ' good' : ' bad';
  const text = kind === 'rate' ? `${amount} ${amount === 1 ? 'pt' : 'pts'}` : kind === 'seconds' ? clock(amount) : prev === 0 ? count(amount) : `${amount}%`;
  return (
    <span className={`kpi-change${tone}`} title={`${up ? 'Up' : 'Down'} ${text} compared with ${label}`}>
      <Icon name={up ? 'arrowUp' : 'arrowDown'} size={13} strokeWidth={2.4} label={up ? 'Up' : 'Down'} />
      {text}
    </span>
  );
}

/** "Eastern Standard Time" for "America/New_York". */
function zoneName(tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date()).find((x) => x.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

function Tile({ label, value, foot, change }: { label: string; value: ReactNode; foot?: ReactNode; change: ReactNode }) {
  return (
    <div className="kpi report-kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value report-value">{value}</span>
      {foot && <span className="kpi-foot">{foot}</span>}
      {change}
    </div>
  );
}

// --- CSV --------------------------------------------------------------------------------------------

function csvOf(r: QueueReport, queueName: string): string {
  const esc = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const row = (cells: (string | number | null)[]) => cells.map(esc).join(',');
  const rate = (n: number | null) => (n === null ? null : Math.round(n * 1000) / 10);
  const lines = [
    row([`Queue report: ${queueName}`, `${r.from} to ${r.to}`, `Times in ${r.timeZone}`]),
    '',
    row([r.by === 'hour' ? 'Hour' : 'Day', 'Calls', 'Answered', 'Not answered', 'Hung up waiting', 'Answered %', 'Within target % (of all calls)', 'Average wait (s)', 'Longest wait (s)', 'Average talk (s)']),
    ...r.series.map((p) =>
      row([r.by === 'hour' ? `${p.key}:00` : p.key, p.offered, p.answered, notAnswered(p), p.abandoned, rate(p.answerRate), rate(p.withinTarget), p.avgWaitSeconds, p.maxWaitSeconds, p.avgTalkSeconds])
    ),
    row([
      'Total',
      r.totals.offered,
      r.totals.answered,
      notAnswered(r.totals),
      r.totals.abandoned,
      rate(r.totals.answerRate),
      rate(r.totals.withinTarget),
      r.totals.avgWaitSeconds,
      r.totals.maxWaitSeconds,
      r.totals.avgTalkSeconds
    ])
  ];
  if (r.agents) {
    lines.push('', row(['Agent', 'Extension', 'Answered', 'Share %', 'Talk time (s)', 'Average talk (s)', 'Average hold (s)']));
    for (const a of r.agents) lines.push(row([a.name, a.extension, a.answered, Math.round(a.share * 1000) / 10, a.talkSeconds, a.avgTalkSeconds, a.avgHoldSeconds]));
  }
  return `${lines.join('\n')}\n`;
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// --- the page -----------------------------------------------------------------------------------------

export const CALL_SERIES: ChartSeries[] = [
  { name: 'Answered', color: 'var(--chart-1)' },
  { name: 'Not answered', color: 'var(--chart-2)' }
];

type AgentSort = 'answered' | 'name' | 'talkSeconds' | 'avgTalkSeconds' | 'avgHoldSeconds';

/** Queue reports: calls (from the phone system) or texts (1.12.32, Stitch's own), as the page's `view` says. */
export function TenantReports() {
  const [params] = useSearchParams();
  return params.get('view') === 'texts' ? <TextReports /> : <CallReports />;
}

/** Calls or texts: the same period and queue carry over. */
function ReportView({ view }: { view: 'calls' | 'texts' }) {
  const [, setParams] = useSearchParams();
  const choose = (v: 'calls' | 'texts') =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        if (v === 'texts') next.set('view', 'texts');
        else next.delete('view');
        // The queue chosen for calls is the one for texts too (texts also have ring groups: their own choice).
        const q = next.get('queue');
        if (v === 'texts' && q && !next.get('team')) next.set('team', q);
        return next;
      },
      { replace: true }
    );
  return (
    <div className="segmented report-view" role="group" aria-label="Report">
      {(['calls', 'texts'] as const).map((v) => (
        <button key={v} type="button" aria-pressed={view === v} className={view === v ? 'on' : undefined} onClick={() => choose(v)}>
          <Icon name={v === 'calls' ? 'phone' : 'chat'} size={14} /> {v === 'calls' ? 'Calls' : 'Texts'}
        </button>
      ))}
    </div>
  );
}

/** Queue reports: calls, waits and agents over a period, for one queue or all of them. */
function CallReports() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const [params, setParams] = useSearchParams();
  const preset = (PRESETS.some((p) => p.id === params.get('range')) ? params.get('range') : '7d') as Preset;
  const queue = params.get('queue') ?? '';
  // "Today" is the phone system's today (it keeps call totals by its own day), wherever the person
  // reading is. Usually the company's too; the report says when it isn't (3136).
  const companyToday = todayIn(tenant.callsTimeZone ?? tenant.timeZone ?? displayZone());
  const { from, to } = rangeOf(preset, params.get('from'), params.get('to'), companyToday);
  const report = useData(() => api.queueReport(d, { from, to, ...(queue ? { queue } : {}) }), [d, from, to, queue]);
  const [tables, setTables] = useState<Record<string, boolean>>({});
  const [sort, setSort] = useState<{ by: AgentSort; desc: boolean }>({ by: 'answered', desc: true });

  const set = (changes: Record<string, string | null>) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        for (const [k, v] of Object.entries(changes)) (v === null || v === '' ? next.delete(k) : next.set(k, v));
        return next;
      },
      { replace: true }
    );

  const r = report.data;
  // Hour by hour, the night before the first call is empty: start an hour before it.
  const shown = useMemo(() => {
    if (!r) return [];
    if (r.by !== 'hour') return r.series;
    const first = r.series.findIndex((pt) => pt.offered > 0);
    return first > 1 ? r.series.slice(first - 1) : r.series;
  }, [r]);
  const unavailable = report.error instanceof ApiError && report.error.code === 'NOT_AVAILABLE';
  const queueName = r ? (r.queue ? (r.queues.find((q) => q.extension === r.queue)?.name ?? r.queue) : 'All queues') : '';
  const prevLabel = r?.previous?.label ?? 'before';
  const p = r?.previous?.totals ?? null;

  const agents = useMemo(() => {
    if (!r?.agents) return null;
    const list = [...r.agents];
    list.sort((a, b) => {
      const va = a[sort.by] ?? -1;
      const vb = b[sort.by] ?? -1;
      const c = typeof va === 'string' ? va.localeCompare(String(vb)) : (va as number) - (vb as number);
      return sort.desc ? -c : c;
    });
    return list;
  }, [r, sort]);
  const topShare = Math.max(0, ...(r?.agents ?? []).map((a) => a.share));

  const toggle = (id: string) => setTables((t) => ({ ...t, [id]: !t[id] }));
  const tableButton = (id: string) => (
    <button type="button" className="link-btn small" aria-pressed={Boolean(tables[id])} onClick={() => toggle(id)}>
      {tables[id] ? 'Show chart' : 'Show table'}
    </button>
  );
  const sortHeader = (by: AgentSort, label: string, numeric = true) => (
    <th scope="col" className={numeric ? 'num' : undefined} aria-sort={sort.by === by ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="sort-btn" onClick={() => setSort((s) => ({ by, desc: s.by === by ? !s.desc : by !== 'name' }))}>
        {label}
        {sort.by === by && <Icon name="chev" size={13} strokeWidth={2.4} />}
      </button>
    </th>
  );

  return (
    <>
      <PageHeader
        eyebrow="Contact center"
        title="Queue reports"
        actions={
          r && (
            <div className="report-actions">
              <Button icon="download" onClick={() => download(`queue-report-${r.from}-to-${r.to}.csv`, csvOf(r, queueName))}>
                Download CSV
              </Button>
              <Button variant="ghost" icon="printer" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          )
        }
      />
      <div className="content report">
        <div className="report-filters" role="group" aria-label="Report period and queue">
          <ReportView view="calls" />
          <div className="segmented" role="group" aria-label="Period">
            {PRESETS.map((x) => (
              <button
                key={x.id}
                type="button"
                aria-pressed={preset === x.id}
                className={preset === x.id ? 'on' : undefined}
                onClick={() => set(x.id === 'custom' ? { range: 'custom', from, to } : { range: x.id, from: null, to: null })}
              >
                {x.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="report-dates">
              <label>
                <span>From</span>
                <input type="date" className="input" value={from} max={to} onChange={(e) => e.target.value && set({ from: e.target.value })} />
              </label>
              <label>
                <span>To</span>
                <input type="date" className="input" value={to} min={from} onChange={(e) => e.target.value && set({ to: e.target.value })} />
              </label>
            </div>
          )}
          <label className="report-queue">
            <span className="sr-only">Queue</span>
            <select className="input" value={queue} onChange={(e) => set({ queue: e.target.value || null })}>
              <option value="">All queues</option>
              {(r?.queues ?? []).map((q) => (
                <option key={q.extension} value={q.extension}>
                  {q.name} ({q.extension})
                </option>
              ))}
            </select>
          </label>
        </div>

        {unavailable ? (
          <Notice title="Queue reports aren’t switched on for this phone system yet">Stitch is confirming how your phone system shares queue figures.</Notice>
        ) : report.error && !r ? (
          report.error instanceof ApiError && report.error.status === 400 ? (
            <Notice tone="warn">{report.error.message}</Notice>
          ) : (
            <Card>
              <LoadError error={report.error} retry={report.reload} />
            </Card>
          )
        ) : !r ? (
          <Card>
            <Loading label="Reading queue figures" />
          </Card>
        ) : r.queues.length === 0 ? (
          <Card>
            <Empty title="No call queues yet">Reports show here once your company has a call queue.</Empty>
          </Card>
        ) : (
          <div className={`report-body${report.loading ? ' refreshing' : ''}`} aria-busy={report.loading}>
            {report.error && <Notice tone="warn">{report.error.message}</Notice>}
            <p className="report-scope">
              <strong>{rangeTitle(r.from, r.to)}</strong> · {queueName} · compared with {prevLabel}
            </p>
            {r.partial && (
              <Notice tone="warn" title="Some figures couldn’t be read just now">
                What’s shown is what the phone system answered. Try again in a minute for the rest.
              </Notice>
            )}
            {/* A queue with no agents has no figures: say so plainly, not "try again" (3140). */}
            {r.noAgents && r.noAgents.length > 0 && (
              <Notice title={r.noAgents.length === 1 ? `${r.noAgents[0]!.name} has no agents` : `${r.noAgents.length} queues have no agents`}>
                Stitch reads a queue’s figures through its agents, so there’s nothing to show for{' '}
                {r.noAgents.length === 1
                  ? 'it until someone is added to it'
                  : `${new Intl.ListFormat('en', { type: 'conjunction' }).format(r.noAgents.map((q) => q.name))} until someone is added to each`}{' '}
                in Call queues.
              </Notice>
            )}

            <section className="kpis report-kpis" aria-label="Totals">
              <Tile
                label="Calls"
                value={count(r.totals.offered)}
                foot={`${count(r.totals.answered)} answered`}
                change={<Change cur={r.totals.offered} prev={p?.offered ?? null} kind="count" better="none" label={prevLabel} />}
              />
              <Tile
                label="Answered"
                value={pct(r.totals.answerRate)}
                foot={`${count(r.totals.answered)} of ${count(r.totals.offered)}`}
                change={<Change cur={r.totals.answerRate} prev={p?.answerRate ?? null} kind="rate" better="up" label={prevLabel} />}
              />
              <Tile
                label="Answered within target"
                value={pct(r.totals.withinTarget)}
                foot="Of all calls, those answered within the queue's target wait"
                change={<Change cur={r.totals.withinTarget} prev={p?.withinTarget ?? null} kind="rate" better="up" label={prevLabel} />}
              />
              <Tile
                label="Average wait"
                value={clock(r.totals.avgWaitSeconds)}
                foot={`Longest ${clock(r.totals.maxWaitSeconds)}`}
                change={<Change cur={r.totals.avgWaitSeconds} prev={p?.avgWaitSeconds ?? null} kind="seconds" better="down" label={prevLabel} />}
              />
              <Tile
                label="Not answered"
                value={count(notAnswered(r.totals))}
                foot={`Calls less those answered · ${count(r.totals.abandoned)} hung up waiting`}
                change={<Change cur={notAnswered(r.totals)} prev={p ? notAnswered(p) : null} kind="count" better="down" label={prevLabel} />}
              />
              <Tile
                label="Average talk time"
                value={clock(r.totals.avgTalkSeconds)}
                foot={`${span(r.totals.talkSeconds)} on calls in all`}
                change={<Change cur={r.totals.avgTalkSeconds} prev={p?.avgTalkSeconds ?? null} kind="seconds" better="none" label={prevLabel} />}
              />
            </section>

            <Card title={r.by === 'hour' ? 'Calls by hour' : 'Calls by day'} action={tableButton('calls')}>
              <div className="card-body">
                {tables.calls ? (
                  <SeriesTable r={r} />
                ) : (
                  <>
                    <ChartLegend series={CALL_SERIES} />
                    <ColumnChart
                      label={`${r.by === 'hour' ? 'Calls by hour' : 'Calls by day'}, answered and not answered`}
                      series={CALL_SERIES}
                      points={shown.map((pt) => ({
                        key: pt.key,
                        ...pointNames(r, pt.key),
                        values: [pt.answered, notAnswered(pt)],
                        detail: pt.offered ? `${count(notAnswered(pt))} not answered · ${count(pt.abandoned)} hung up waiting · average wait ${clock(pt.avgWaitSeconds)}` : 'No calls'
                      }))}
                    />
                  </>
                )}
              </div>
            </Card>

            <div className="report-pair">
              <Card title="Average wait" action={tableButton('wait')}>
                <div className="card-body">
                  {tables.wait ? (
                    <SeriesTable r={r} only="wait" />
                  ) : (
                    <LineChart
                      label={`Average wait ${r.by === 'hour' ? 'by hour' : 'by day'}`}
                      color="var(--chart-1)"
                      format={(n) => clock(Math.round(n))}
                      points={shown.map((pt) => ({ key: pt.key, ...pointNames(r, pt.key), value: pt.avgWaitSeconds }))}
                    />
                  )}
                </div>
              </Card>
              <Card title="Answered within target" action={tableButton('target')}>
                <div className="card-body">
                  {tables.target ? (
                    <SeriesTable r={r} only="target" />
                  ) : (
                    <LineChart
                      label={`Share answered within target ${r.by === 'hour' ? 'by hour' : 'by day'}`}
                      color="var(--chart-1)"
                      format={(n) => `${Math.round(n * 100)}%`}
                      max={1}
                      points={shown.map((pt) => ({ key: pt.key, ...pointNames(r, pt.key), value: pt.withinTarget }))}
                    />
                  )}
                </div>
              </Card>
            </div>

            {!r.queue && r.byQueue.length > 1 && (
              <Card title="Queues">
                <div className="table-wrap">
                  <table className="table report-table">
                    <thead>
                      <tr>
                        <th scope="col">Queue</th>
                        <th scope="col" className="num">
                          Calls
                        </th>
                        <th scope="col" className="num">
                          Answered
                        </th>
                        <th scope="col" className="num">
                          Within target
                        </th>
                        <th scope="col" className="num">
                          Average wait
                        </th>
                        <th scope="col" className="num">
                          Longest wait
                        </th>
                        <th scope="col" className="num">
                          Not answered
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.byQueue.map((q) => (
                        <tr key={q.extension}>
                          <td>
                            <button type="button" className="link-btn" onClick={() => set({ queue: q.extension })}>
                              {q.name}
                            </button>
                            <small className="muted"> {q.extension}</small>
                            {q.noAgents && <small className="muted"> · no agents</small>}
                          </td>
                          <td className="num">{count(q.metrics.offered)}</td>
                          <td className="num">{pct(q.metrics.answerRate)}</td>
                          <td className="num">{pct(q.metrics.withinTarget)}</td>
                          <td className="num">{clock(q.metrics.avgWaitSeconds)}</td>
                          <td className="num">{clock(q.metrics.maxWaitSeconds)}</td>
                          <td className="num">{count(notAnswered(q.metrics))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Card title="Agents">
              {!agents ? (
                <div className="card-body">
                  <span className="muted">Agent figures cover up to a month at a time, or whole months. Choose a shorter period, or Last month, to see them.</span>
                </div>
              ) : agents.length === 0 ? (
                <Empty title="No agents">Nobody is set up to take calls from {queueName === 'All queues' ? 'these queues' : queueName}.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table report-table">
                    <thead>
                      <tr>
                        {sortHeader('name', 'Agent', false)}
                        {sortHeader('answered', 'Answered')}
                        <th scope="col" className="share-col">
                          Share of answered calls
                        </th>
                        {sortHeader('talkSeconds', 'Time on calls')}
                        {sortHeader('avgTalkSeconds', 'Average call')}
                        {sortHeader('avgHoldSeconds', 'Average hold')}
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((a) => (
                        <tr key={a.extension}>
                          <td>
                            <span className="cell-stack">
                              <strong style={{ fontWeight: 650 }}>{a.name}</strong>
                              <small className="muted">Ext {a.extension}</small>
                            </span>
                          </td>
                          <td className="num">{count(a.answered)}</td>
                          <td className="share-col">
                            <span className="share">
                              <span className="share-track" aria-hidden="true">
                                <span className="share-bar" style={{ width: `${topShare ? (a.share / topShare) * 100 : 0}%` }} />
                              </span>
                              <span className="share-value">{pct(a.share)}</span>
                            </span>
                          </td>
                          <td className="num">{span(a.talkSeconds)}</td>
                          <td className="num">{clock(a.avgTalkSeconds)}</td>
                          <td className="num">{clock(a.avgHoldSeconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <p className="muted small-print report-foot">
              Figures from your phone system, in {zoneName(r.timeZone)}
              {r.companyTimeZone && `: it keeps calls by its own day and hour, so days here start at midnight ${zoneName(r.timeZone)}, not ${zoneName(r.companyTimeZone)}`}. Arrows
              compare with {prevLabel}. Read {clockTime(r.generatedAt)}.
              {r.by === 'hour' && r.to === iso(companyToday) && ' Today’s figures update every minute.'}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

/** The table twin of the charts: every value, no hovering needed. */
function SeriesTable({ r, only }: { r: QueueReport; only?: 'wait' | 'target' }) {
  return (
    <div className="table-wrap">
      <table className="table report-table">
        <thead>
          <tr>
            <th scope="col">{r.by === 'hour' ? 'Hour' : 'Day'}</th>
            {!only && (
              <>
                <th scope="col" className="num">
                  Calls
                </th>
                <th scope="col" className="num">
                  Answered
                </th>
                <th scope="col" className="num">
                  Not answered
                </th>
                <th scope="col" className="num">
                  Hung up waiting
                </th>
              </>
            )}
            {(!only || only === 'wait') && (
              <>
                <th scope="col" className="num">
                  Average wait
                </th>
                <th scope="col" className="num">
                  Longest wait
                </th>
              </>
            )}
            {(!only || only === 'target') && (
              <th scope="col" className="num">
                Within target
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {r.series.map((pt) => (
            <tr key={pt.key}>
              <td>{pointNames(r, pt.key).title}</td>
              {!only && (
                <>
                  <td className="num">{count(pt.offered)}</td>
                  <td className="num">{count(pt.answered)}</td>
                  <td className="num">{count(notAnswered(pt))}</td>
                  <td className="num">{count(pt.abandoned)}</td>
                </>
              )}
              {(!only || only === 'wait') && (
                <>
                  <td className="num">{clock(pt.avgWaitSeconds)}</td>
                  <td className="num">{clock(pt.maxWaitSeconds)}</td>
                </>
              )}
              {(!only || only === 'target') && <td className="num">{pct(pt.withinTarget)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- texts (1.12.32) ----------------------------------------------------------------------------------

export const TEXT_SERIES: ChartSeries[] = [{ name: 'Conversations', color: 'var(--chart-1)' }];
const teamWord = (kind: 'ringGroup' | 'callQueue') => (kind === 'ringGroup' ? 'ring group' : 'queue');
const shareOf = (part: number, whole: number) => (whole ? part / whole : null);

function csvOfTexts(r: TextReport, teamName: string): string {
  const esc = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const row = (cells: (string | number | null)[]) => cells.map(esc).join(',');
  const rate = (n: number | null) => (n === null ? null : Math.round(n * 1000) / 10);
  const t = r.totals;
  const lines = [
    row([`Text report: ${teamName}`, `${r.from} to ${r.to}`, `Times in ${r.timeZone}`]),
    '',
    row([r.by === 'hour' ? 'Hour' : 'Day', 'Conversations', 'Answered', 'Average first reply (s)', 'Within reply time %', 'Resolved']),
    ...r.series.map((p) => row([r.by === 'hour' ? `${p.key}:00` : p.key, p.conversations, p.answered, p.avgFirstReplySeconds, rate(p.withinReplyRate), p.resolved])),
    row(['Total', t.conversations, t.answered, t.avgFirstReplySeconds, rate(t.withinReplyRate), t.resolved]),
    '',
    row(['Longest first reply (s)', 'Handed on', 'Moved on by the timers', 'Supervisors told', 'Automatic texts']),
    row([t.maxFirstReplySeconds, t.handed, t.movedOn, t.escalated, t.autoTexts])
  ];
  if (r.people.length) {
    lines.push('', row(['Person', 'Answered', 'Average first reply (s)', 'Within reply time', 'Resolved']));
    for (const p of r.people) lines.push(row([p.name, p.answered, p.avgFirstReplySeconds, p.withinReply, p.resolved]));
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Texts in Reports: a ring group's or queue's texts (or every one's) over a period, from Stitch's own
 * records of each conversation: how many came in, how fast the first answer came, resolved, handed
 * on, and each person's part. The same periods and look as the call report.
 */
function TextReports() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const [params, setParams] = useSearchParams();
  const preset = (PRESETS.some((p) => p.id === params.get('range')) ? params.get('range') : '7d') as Preset;
  const team = params.get('team') ?? '';
  const companyToday = todayIn(tenant.timeZone ?? displayZone());
  const { from, to } = rangeOf(preset, params.get('from'), params.get('to'), companyToday);
  const report = useData(() => api.textReport(d, { from, to, ...(team ? { team } : {}) }), [d, from, to, team], { live: false });
  const [tables, setTables] = useState<Record<string, boolean>>({});

  const set = (changes: Record<string, string | null>) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        for (const [k, v] of Object.entries(changes)) (v === null || v === '' ? next.delete(k) : next.set(k, v));
        return next;
      },
      { replace: true }
    );

  const r = report.data;
  // Hour by hour, the night before the first text is empty: start an hour before it.
  const shown = useMemo(() => {
    if (!r) return [];
    if (r.by !== 'hour') return r.series;
    const first = r.series.findIndex((pt) => pt.conversations > 0 || pt.answered > 0);
    return first > 1 ? r.series.slice(first - 1) : r.series;
  }, [r]);
  const teamName = r ? (r.team ? (r.teams.find((x) => x.extension === r.team)?.name ?? r.team) : 'All ring groups and queues') : '';
  const prevLabel = r?.previous.label ?? 'before';
  const p: TextMetrics | null = r?.previous.totals ?? null;
  const t = r?.totals;
  const empty = t ? t.conversations + t.answered + t.resolved + t.handed + t.autoTexts === 0 : false;
  const within = r?.replyMinutes ? `Answered within ${r.replyMinutes} min` : 'Answered within reply time';

  const toggle = (id: string) => setTables((x) => ({ ...x, [id]: !x[id] }));
  const tableButton = (id: string) => (
    <button type="button" className="link-btn small" aria-pressed={Boolean(tables[id])} onClick={() => toggle(id)}>
      {tables[id] ? 'Show chart' : 'Show table'}
    </button>
  );

  return (
    <>
      <PageHeader
        eyebrow="Contact center"
        title="Queue reports"
        actions={
          r &&
          !empty && (
            <div className="report-actions">
              <Button icon="download" onClick={() => download(`text-report-${r.from}-to-${r.to}.csv`, csvOfTexts(r, teamName))}>
                Download CSV
              </Button>
              <Button variant="ghost" icon="printer" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          )
        }
      />
      <div className="content report">
        <div className="report-filters" role="group" aria-label="Report period and team">
          <ReportView view="texts" />
          <div className="segmented" role="group" aria-label="Period">
            {PRESETS.map((x) => (
              <button
                key={x.id}
                type="button"
                aria-pressed={preset === x.id}
                className={preset === x.id ? 'on' : undefined}
                onClick={() => set(x.id === 'custom' ? { range: 'custom', from, to } : { range: x.id, from: null, to: null })}
              >
                {x.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="report-dates">
              <label>
                <span>From</span>
                <input type="date" className="input" value={from} max={to} onChange={(e) => e.target.value && set({ from: e.target.value })} />
              </label>
              <label>
                <span>To</span>
                <input type="date" className="input" value={to} min={from} onChange={(e) => e.target.value && set({ to: e.target.value })} />
              </label>
            </div>
          )}
          <label className="report-queue">
            <span className="sr-only">Ring group or queue</span>
            <select className="input" value={team} onChange={(e) => set({ team: e.target.value || null })}>
              <option value="">All ring groups and queues</option>
              {(r?.teams ?? []).map((x) => (
                <option key={x.extension} value={x.extension}>
                  {x.name} ({teamWord(x.kind)} {x.extension})
                </option>
              ))}
            </select>
          </label>
        </div>

        {report.error && !r ? (
          report.error instanceof ApiError && report.error.status === 400 ? (
            <Notice tone="warn">{report.error.message}</Notice>
          ) : (
            <Card>
              <LoadError error={report.error} retry={report.reload} />
            </Card>
          )
        ) : !r || !t ? (
          <Card>
            <Loading label="Counting texts" />
          </Card>
        ) : r.teams.length === 0 ? (
          <Card>
            <Empty title="No ring group or queue takes texts yet">
              Send a number’s texts to a ring group or queue, and its texts are counted here. <Link to="../numbers">Go to Numbers</Link>
            </Empty>
          </Card>
        ) : (
          <div className={`report-body${report.loading ? ' refreshing' : ''}`} aria-busy={report.loading}>
            {report.error && <Notice tone="warn">{report.error.message}</Notice>}
            <p className="report-scope">
              <strong>{rangeTitle(r.from, r.to)}</strong> · {teamName} · compared with {prevLabel}
            </p>
            {empty ? (
              <Card>
                <Empty title="No texts in this period">Texts to {r.team ? teamName : 'your ring groups and queues'} are counted here as they come in and are answered.</Empty>
              </Card>
            ) : (
              <>
                <section className="kpis report-kpis" aria-label="Totals">
                  <Tile
                    label="Conversations"
                    value={count(t.conversations)}
                    foot={`${count(t.answered)} answered`}
                    change={<Change cur={t.conversations} prev={p?.conversations ?? null} kind="count" better="none" label={prevLabel} />}
                  />
                  <Tile
                    label={within}
                    value={pct(t.withinReplyRate)}
                    foot={`${count(t.withinReply)} of ${count(t.answered)} first replies`}
                    change={<Change cur={t.withinReplyRate} prev={p?.withinReplyRate ?? null} kind="rate" better="up" label={prevLabel} />}
                  />
                  <Tile
                    label="Average first reply"
                    value={clock(t.avgFirstReplySeconds)}
                    foot={`Longest ${clock(t.maxFirstReplySeconds)}`}
                    change={<Change cur={t.avgFirstReplySeconds} prev={p?.avgFirstReplySeconds ?? null} kind="seconds" better="down" label={prevLabel} />}
                  />
                  <Tile
                    label="Resolved"
                    value={count(t.resolved)}
                    foot={t.escalated ? `${count(t.escalated)} waited long enough to tell supervisors` : 'None waited long enough to tell supervisors'}
                    change={<Change cur={t.resolved} prev={p?.resolved ?? null} kind="count" better="none" label={prevLabel} />}
                  />
                  <Tile
                    label="Handed on"
                    value={count(t.handed)}
                    foot={`${count(t.movedOn)} moved on by the timers`}
                    change={<Change cur={t.handed} prev={p?.handed ?? null} kind="count" better="none" label={prevLabel} />}
                  />
                  <Tile
                    label="Automatic texts"
                    value={count(t.autoTexts)}
                    foot="Place in line, after hours and opening"
                    change={<Change cur={t.autoTexts} prev={p?.autoTexts ?? null} kind="count" better="none" label={prevLabel} />}
                  />
                </section>

                <Card title={r.by === 'hour' ? 'Texts by hour' : 'Texts by day'} action={tableButton('texts')}>
                  <div className="card-body">
                    {tables.texts ? (
                      <TextSeriesTable r={r} />
                    ) : (
                      <ColumnChart
                        label={`New conversations ${r.by === 'hour' ? 'by hour' : 'by day'}`}
                        series={TEXT_SERIES}
                        points={shown.map((pt) => ({
                          key: pt.key,
                          ...pointNames(r, pt.key),
                          values: [pt.conversations],
                          detail: `${count(pt.answered)} answered · ${count(pt.resolved)} resolved${pt.avgFirstReplySeconds !== null ? ` · first reply ${clock(pt.avgFirstReplySeconds)}` : ''}`
                        }))}
                      />
                    )}
                  </div>
                </Card>

                <div className="report-pair">
                  <Card title="Average first reply" action={tableButton('reply')}>
                    <div className="card-body">
                      {tables.reply ? (
                        <TextSeriesTable r={r} only="reply" />
                      ) : (
                        <LineChart
                          label={`Average first reply ${r.by === 'hour' ? 'by hour' : 'by day'}`}
                          color="var(--chart-1)"
                          format={(n) => clock(Math.round(n))}
                          empty="No answers"
                          points={shown.map((pt) => ({ key: pt.key, ...pointNames(r, pt.key), value: pt.avgFirstReplySeconds }))}
                        />
                      )}
                    </div>
                  </Card>
                  <Card title={within} action={tableButton('within')}>
                    <div className="card-body">
                      {tables.within ? (
                        <TextSeriesTable r={r} only="within" />
                      ) : (
                        <LineChart
                          label={`Share of first replies within the reply time ${r.by === 'hour' ? 'by hour' : 'by day'}`}
                          color="var(--chart-1)"
                          format={(n) => `${Math.round(n * 100)}%`}
                          max={1}
                          empty="No answers"
                          points={shown.map((pt) => ({ key: pt.key, ...pointNames(r, pt.key), value: pt.withinReplyRate }))}
                        />
                      )}
                    </div>
                  </Card>
                </div>

                {!r.team && r.byTeam.length > 1 && (
                  <Card title="Ring groups and queues">
                    <div className="table-wrap">
                      <table className="table report-table">
                        <thead>
                          <tr>
                            <th scope="col">Ring group or queue</th>
                            <th scope="col" className="num">
                              Conversations
                            </th>
                            <th scope="col" className="num">
                              Answered
                            </th>
                            <th scope="col" className="num">
                              Within reply time
                            </th>
                            <th scope="col" className="num">
                              First reply
                            </th>
                            <th scope="col" className="num">
                              Resolved
                            </th>
                            <th scope="col" className="num">
                              Handed on
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.byTeam.map((x) => (
                            <tr key={x.extension}>
                              <td>
                                <button type="button" className="link-btn" onClick={() => set({ team: x.extension })}>
                                  {x.name}
                                </button>
                                <small className="muted">
                                  {' '}
                                  {teamWord(x.kind)} {x.extension}
                                </small>
                              </td>
                              <td className="num">{count(x.metrics.conversations)}</td>
                              <td className="num">{count(x.metrics.answered)}</td>
                              <td className="num">{pct(x.metrics.withinReplyRate)}</td>
                              <td className="num">{clock(x.metrics.avgFirstReplySeconds)}</td>
                              <td className="num">{count(x.metrics.resolved)}</td>
                              <td className="num">{count(x.metrics.handed)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                <Card title="People">
                  {r.people.length === 0 ? (
                    <Empty title="Nobody answered or resolved texts in this period" />
                  ) : (
                    <div className="table-wrap">
                      <table className="table report-table">
                        <thead>
                          <tr>
                            <th scope="col">Person</th>
                            <th scope="col" className="num">
                              Answered
                            </th>
                            <th scope="col" className="num">
                              Average first reply
                            </th>
                            <th scope="col" className="num">
                              Within reply time
                            </th>
                            <th scope="col" className="num">
                              Resolved
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.people.map((x) => (
                            <tr key={x.id}>
                              <td>
                                <strong style={{ fontWeight: 650 }}>{x.name}</strong>
                              </td>
                              <td className="num">{count(x.answered)}</td>
                              <td className="num">{clock(x.avgFirstReplySeconds)}</td>
                              <td className="num">{pct(shareOf(x.withinReply, x.answered))}</td>
                              <td className="num">{count(x.resolved)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </>
            )}

            <p className="muted small-print report-foot">
              From Stitch’s own record of each conversation, in {zoneName(r.timeZone)}. First reply: from their first text to the first answer, or from when the
              team opened for a text that came while it was closed. In time: against each team’s reply time as it is now, so changing it changes past periods too.
              {!empty && ` Arrows compare with ${prevLabel}.`} Read {clockTime(r.generatedAt)}.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

/** The texts charts' table twin: every value, no hovering needed. */
function TextSeriesTable({ r, only }: { r: TextReport; only?: 'reply' | 'within' }) {
  return (
    <div className="table-wrap">
      <table className="table report-table">
        <thead>
          <tr>
            <th scope="col">{r.by === 'hour' ? 'Hour' : 'Day'}</th>
            {!only && (
              <>
                <th scope="col" className="num">
                  Conversations
                </th>
                <th scope="col" className="num">
                  Answered
                </th>
                <th scope="col" className="num">
                  Resolved
                </th>
              </>
            )}
            {(!only || only === 'reply') && (
              <th scope="col" className="num">
                Average first reply
              </th>
            )}
            {(!only || only === 'within') && (
              <th scope="col" className="num">
                Within reply time
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {r.series.map((pt) => (
            <tr key={pt.key}>
              <td>{pointNames(r, pt.key).title}</td>
              {!only && (
                <>
                  <td className="num">{count(pt.conversations)}</td>
                  <td className="num">{count(pt.answered)}</td>
                  <td className="num">{count(pt.resolved)}</td>
                </>
              )}
              {(!only || only === 'reply') && <td className="num">{clock(pt.avgFirstReplySeconds)}</td>}
              {(!only || only === 'within') && <td className="num">{pct(pt.withinReplyRate)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type { ReportMetrics };
